import React, { useEffect, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { Loader2, CheckCircle2, XCircle, Shield, Zap, Building2, Crown } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SubscriptionSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token, refreshUser } = useAuth();
  const { toast } = useToast();
  
  const [status, setStatus] = useState('checking'); // checking, success, error
  const [tier, setTier] = useState(null);
  const [pollCount, setPollCount] = useState(0);
  
  const sessionId = searchParams.get('session_id');
  
  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }
    
    if (!token) {
      // Wait for auth to be ready
      return;
    }
    
    const pollStatus = async () => {
      try {
        const res = await fetch(`${API_URL}/api/stripe/checkout-status/${sessionId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (res.ok) {
          const data = await res.json();
          
          if (data.payment_status === 'paid') {
            setTier(data.tier);
            setStatus('success');
            
            // Refresh user data
            if (refreshUser) {
              await refreshUser();
            }
            
            toast({
              title: `Welcome to VeriWatch ${data.tier?.charAt(0).toUpperCase() + data.tier?.slice(1)}!`,
              description: 'Your subscription is now active.',
            });
            return;
          } else if (data.status === 'expired') {
            setStatus('error');
            return;
          }
        }
        
        // Continue polling if not complete
        if (pollCount < 10) {
          setPollCount(prev => prev + 1);
          setTimeout(pollStatus, 2000);
        } else {
          setStatus('error');
        }
      } catch (err) {
        console.error('Failed to check status:', err);
        if (pollCount < 10) {
          setPollCount(prev => prev + 1);
          setTimeout(pollStatus, 2000);
        } else {
          setStatus('error');
        }
      }
    };
    
    pollStatus();
  }, [sessionId, token, pollCount, refreshUser, toast]);
  
  const getTierIcon = () => {
    switch(tier) {
      case 'basic': return <Zap className="w-16 h-16 text-blue-400" />;
      case 'pro': return <Zap className="w-16 h-16 text-teal-400" />;
      case 'business': return <Building2 className="w-16 h-16 text-purple-400" />;
      case 'lifetime': return <Crown className="w-16 h-16 text-amber-400" />;
      default: return <Shield className="w-16 h-16 text-teal-400" />;
    }
  };
  
  const getTierColor = () => {
    switch(tier) {
      case 'basic': return 'text-blue-400';
      case 'pro': return 'text-teal-400';
      case 'business': return 'text-purple-400';
      case 'lifetime': return 'text-amber-400';
      default: return 'text-teal-400';
    }
  };
  
  return (
    <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center p-4">
      <div className="max-w-md w-full text-center">
        {status === 'checking' && (
          <div className="space-y-6">
            <Loader2 className="w-16 h-16 animate-spin text-teal-400 mx-auto" />
            <h1 className="text-2xl font-bold">Processing your payment...</h1>
            <p className="text-slate-400">Please wait while we confirm your subscription.</p>
            <div className="text-sm text-slate-500">
              Attempt {pollCount + 1} of 10
            </div>
          </div>
        )}
        
        {status === 'success' && (
          <div className="space-y-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-24 h-24 bg-emerald-500/20 rounded-full animate-ping" />
              </div>
              <CheckCircle2 className="w-24 h-24 text-emerald-400 mx-auto relative" />
            </div>
            
            <h1 className="text-3xl font-bold">Welcome Aboard!</h1>
            
            <div className="bg-slate-900/50 rounded-xl p-6 border border-slate-800">
              {getTierIcon()}
              <h2 className={`text-2xl font-bold mt-4 ${getTierColor()}`}>
                VeriWatch {tier?.charAt(0).toUpperCase() + tier?.slice(1)}
              </h2>
              <p className="text-slate-400 mt-2">
                Your subscription is now active. You have full access to all {tier} features.
              </p>
            </div>
            
            <div className="space-y-3">
              <Link to="/dashboard">
                <Button className="w-full bg-teal-600 hover:bg-teal-700" data-testid="go-to-dashboard">
                  Go to Dashboard
                </Button>
              </Link>
              <Link to="/overwatch">
                <Button variant="outline" className="w-full border-slate-600">
                  Launch Tank's OverWatch
                </Button>
              </Link>
            </div>
            
            <p className="text-sm text-slate-500">
              A confirmation email has been sent to your address.
            </p>
          </div>
        )}
        
        {status === 'error' && (
          <div className="space-y-6">
            <XCircle className="w-16 h-16 text-red-400 mx-auto" />
            <h1 className="text-2xl font-bold">Something went wrong</h1>
            <p className="text-slate-400">
              We couldn't confirm your payment. If you were charged, please contact support.
            </p>
            
            <div className="space-y-3">
              <Link to="/pricing">
                <Button className="w-full bg-teal-600 hover:bg-teal-700">
                  Try Again
                </Button>
              </Link>
              <Link to="/dashboard">
                <Button variant="outline" className="w-full border-slate-600">
                  Go to Dashboard
                </Button>
              </Link>
            </div>
            
            <p className="text-sm text-slate-500">
              Need help? Contact support@veriwatch.io
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionSuccessPage;
